import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Search, Download } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const supporterTypes = [
  { value: "all", label: "All Types" },
  { value: "supporter", label: "Supporters" },
  { value: "volunteer", label: "Volunteers" },
  { value: "sponsor", label: "Sponsors" },
  { value: "partner", label: "Partners" },
  { value: "donor", label: "Donors" }
];

export default function AdminSupporters() {
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchSupporters();
  }, []);

  const fetchSupporters = async () => {
    try {
      const response = await axios.get(`${API}/supporters`, { withCredentials: true });
      setSupporters(response.data);
    } catch (error) {
      console.error("Error fetching supporters:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSupporters = supporters.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          s.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || s.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Type", "Interests", "Joined"];
    const rows = supporters.map(s => [
      s.name,
      s.email,
      s.phone || "",
      s.type,
      s.interests?.join("; ") || "",
      format(new Date(s.created_at), "yyyy-MM-dd")
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supporters-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  return (
    <AdminLayout currentPage="Supporters">
      <div className="p-8" data-testid="admin-supporters">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Supporters</h1>
            <p className="text-slate-600">Manage volunteers, sponsors, and supporters</p>
          </div>
          <Button variant="outline" onClick={exportCSV} data-testid="export-supporters-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search supporters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-supporters"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48" data-testid="type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {supporterTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-20 animate-pulse"></div>
            ))}
          </div>
        ) : filteredSupporters.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Name</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Contact</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Type</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Interests</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSupporters.map(supporter => (
                  <tr key={supporter.supporter_id} data-testid={`supporter-row-${supporter.supporter_id}`}>
                    <td className="p-4">
                      <p className="font-medium text-slate-800">{supporter.name}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-600">{supporter.email}</p>
                      {supporter.phone && (
                        <p className="text-sm text-slate-500">{supporter.phone}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="capitalize bg-awareness-blue/10 text-awareness-blue px-2 py-1 rounded text-sm">
                        {supporter.type}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {supporter.interests?.slice(0, 3).map((interest, i) => (
                          <span key={i} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">
                            {interest}
                          </span>
                        ))}
                        {supporter.interests?.length > 3 && (
                          <span className="text-slate-500 text-xs">+{supporter.interests.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">
                      {format(new Date(supporter.created_at), "MMM d, yyyy")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No supporters yet</h3>
            <p className="text-slate-500">Supporters will appear here when they sign up</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
